import { useMemo } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import { useListingNotes } from "hooks/useListingNotes";
import { eventActivityItems, noteActivityItems } from "utils/listings/listingActivity";
import type { CalendarEvent } from "../../../../@types/sharedSchema";
import { useOfficeNames } from "./ListingActivityIndicator";
import { ListingActivityList } from "./ListingActivityList";

type Props = {
  listingId: string;
  /** The listing's meetings and tasks, the user's own and her office's (`useListingEvents`). */
  events: CalendarEvent[];
};

/**
 * The listing window's activity timeline (Trello #246): every meeting, task and note on the listing —
 * the realtor's own and her office's shared ones — in one list: what is ahead, then what happened.
 * It replaced the office-only section (`ListingOrgActivitySection`), which left out her own activity
 * and so could not answer "what happened on this listing". Renders nothing on an untouched listing.
 */
export function ListingActivitySection({ listingId, events }: Props) {
  const { t } = useTranslation();
  const {
    user: { email }
  } = useAuth();
  const names = useOfficeNames();
  const { notes } = useListingNotes(listingId, true);

  // "Now" is taken with the activity: what is ahead is judged again whenever it changes.
  const { now, items } = useMemo(() => {
    const at = new Date();
    return { now: at, items: [...eventActivityItems(events, email, at), ...noteActivityItems(notes)] };
  }, [events, notes, email]);

  if (items.length === 0) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t("listingActivity.title")}
      </Typography>
      <ListingActivityList items={items} names={names} now={now} />
    </Box>
  );
}
